<template>
  <div>
    <h3 class="mb-4">
      Safeguarding Summary
    </h3>
    <v-sheet
      v-if="loading"
      :color="`grey lighten-4`"
      class="pa-3"
    >
      <v-skeleton-loader
        class="mx-auto d-flex flex-column align-content-space-between"
        min-height="600"
        type="paragraph, paragraph, paragraph, paragraph, paragraph, paragraph, actions"
      />
    </v-sheet>
    <v-card
      v-else-if="summaryData"
      class="pa-2"
      color="#f2f2f2"
    >
      <v-row>
        <CardSafeguardingTenantDetail
          v-for="tenant in summaryData.tenantData"
          :key="tenant.tenantName"
          :name="tenant.tenantName"
          :data="tenant"
          :bank-currency="summaryData.bankCurrency"
          :additional-currency="summaryData.additionalCurrency"
        />
      </v-row>
      <v-divider class="mt-4" />
      <v-card-actions class="d-flex justify-space-between align-center">
        <v-spacer />
        <v-btn
          color="primary"
          :loading="isDownloading"
          @click="openDialog('download')"
        >
          Download
        </v-btn>
        <v-btn
          color="primary"
          :loading="isStoreLoading"
          @click="openDialog('store')"
        >
          Store on Google Drive
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      v-model="dialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title>Enter Balances</v-card-title>
        <v-card-text>
          <v-alert
            type="info"
            dense
          >
            These inputs are optional, you can leave 1 or more empty.
          </v-alert>
          <v-text-field
            v-model="tempFirstAccountUkBalance"
            label="Remittance - Safeguarding Balance for UK (Safe 01)"
          />
          <v-text-field
            v-model="tempFirstAccountEuropeBalance"
            label="Wallet - Safeguarding Balance for EU (Safe 01)"
          />
          <v-text-field
            v-model="tempFirstAccountCaBalance"
            label="Wallet - Safeguarding Balance for Canada (Safe 01)"
          />
          <v-text-field
            v-model="tempSecondAccountUkBalance"
            label="Remittance - Safeguarding Balance for UK (Safe 02)"
          />
          <v-text-field
            v-model="tempSecondAccountEuropeBalance"
            label="Wallet - Safeguarding Balance for EU (Safe 02)"
          />
          <v-text-field
            v-model="tempSecondAccountCaBalance"
            label="Wallet - Safeguarding Balance for Canada (Safe 02)"
          />
          <v-text-field
            v-model="tempTotalBalance"
            label="Total Balance"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="submitValues"
          >
            Submit
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SafeguardingService from "@/services/SafeguardingService";
import CardSafeguardingTenantDetail from "./CardSafeguardingTenantDetail";

export default {
  name: "CardSafeguardingSummary",
  components: {
    CardSafeguardingTenantDetail
  },
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    summaryData: {
      type: Object,
      default: null
    },
    license: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isDownloading: false,
      isStoreLoading: false,
      dialog: false,
      actionType: "",
      tempFirstAccountUkBalance: null,
      tempFirstAccountEuropeBalance: null,
      tempFirstAccountCaBalance: null,
      tempSecondAccountUkBalance: null,
      tempSecondAccountEuropeBalance: null,
      tempSecondAccountCaBalance: null,
      tempTotalBalance: null,
      firstAccountUkBalance: null,
      firstAccountEuropeBalance: null,
      firstAccountCaBalance: null,
      secondAccountUkBalance: null,
      secondAccountEuropeBalance: null,
      secondAccountCaBalance: null,
      totalBalance: null
    };
  },
  methods: {
    openDialog(actionType) {
      this.dialog = true;
      this.actionType = actionType;
    },
    submitValues() {
      this.dialog = false;
      this.firstAccountUkBalance = this.tempFirstAccountUkBalance || null;
      this.firstAccountEuropeBalance = this.tempFirstAccountEuropeBalance || null;
      this.firstAccountCaBalance = this.tempFirstAccountCaBalance || null;
      this.secondAccountUkBalance = this.tempSecondAccountUkBalance || null;
      this.secondAccountEuropeBalance = this.tempSecondAccountEuropeBalance || null;
      this.secondAccountCaBalance = this.tempSecondAccountCaBalance || null;
      this.totalBalance = this.tempTotalBalance || null;

      if (this.actionType === "download") {
        this.downloadFile();
      } else {
        this.storeOnGoogleDrive();
      }
    },
    downloadFile() {
      this.$emit("page-loading", true);
      this.isDownloading = true;
      SafeguardingService.downloadSafeguardingReportFile({
        license: this.license,
        firstAccountUkBalance: this.firstAccountUkBalance,
        firstAccountEuropeBalance: this.firstAccountEuropeBalance,
        firstAccountCaBalance: this.firstAccountCaBalance,
        secondAccountUkBalance: this.secondAccountUkBalance,
        secondAccountEuropeBalance: this.secondAccountEuropeBalance,
        secondAccountCaBalance: this.secondAccountCaBalance,
        totalBalance: this.totalBalance
      })
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `Daily_Cash_Reconciliation_Wallet_${new Date().toISOString()}.xlsx`
          );
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        })
        .finally(() => {
          this.$emit("page-loading", false);
          this.isDownloading = false;
        });
    },
    async storeOnGoogleDrive() {
      this.$emit("page-loading", true);
      this.isStoreLoading = true;
      try {
        const resp = await SafeguardingService.storeSafeguardingReportFileOnDrive({
          license: this.license,
          firstAccountUkBalance: this.firstAccountUkBalance,
          firstAccountEuropeBalance: this.firstAccountEuropeBalance,
          firstAccountCaBalance: this.firstAccountCaBalance,
          secondAccountUkBalance: this.secondAccountUkBalance,
          secondAccountEuropeBalance: this.secondAccountEuropeBalance,
          secondAccountCaBalance: this.secondAccountCaBalance,
          totalBalance: this.totalBalance
        });
        console.log("File stored successfully. Response: ", resp.data);
      } catch (e) {
        console.error(e);
      } finally {
        this.$emit("page-loading", false);
        this.isStoreLoading = false;
      }
    }
  }
};
</script>
